import { Directive, Input, OnChanges, SimpleChanges } from '@angular/core';

import { handleDropdownMenuContainerKeydown, showDropdownAndFocusMenuItem } from 'app/shared/utils/dropdown-menu-keyboard.util';

import { BaseControlDirective } from './base-control.directive';

@Directive()
export abstract class CommonDropdownControlDirective<TItem, TValue> extends BaseControlDirective<TValue> implements OnChanges {
  private static nextId = 0;

  private readonly dropdownId = CommonDropdownControlDirective.nextId++;
  private selectedItemField: TItem | undefined;

  public readonly buttonId = `common-dropdown-button-${this.dropdownId}`;
  public readonly menuId = `common-dropdown-menu-${this.dropdownId}`;

  @Input() public placeholder = '';
  @Input() public emptyText = '';
  @Input() public disabled = false;
  @Input() public testId = '';
  @Input() public items!: TItem[];
  @Input() public getName!: (item: TItem) => string | undefined;
  @Input() public getValue: (item: TItem) => TValue = (item) => item as unknown as TValue;
  @Input() public getBadge: (item: TItem) => string | undefined = () => undefined;

  public get selectedItem(): TItem | undefined {
    return this.selectedItemField;
  }

  public onItemClick(item: TItem): void {
    this.model = this.getValue(item);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.items || changes.getValue) {
      this.syncSelectedItem();
    }
  }

  public override modelChanged(): void {
    super.modelChanged();
    this.syncSelectedItem();
  }

  public override writeValue(model: TValue | undefined | null): void {
    super.writeValue(model);
    this.syncSelectedItem();
  }

  public resolveName(item: TItem | unknown): string {
    return item ? this.getName(item as TItem) ?? '' : this.emptyText || this.placeholder;
  }

  public resolveBadge(item: TItem | unknown): string | undefined {
    return item ? this.getBadge(item as TItem) : undefined;
  }

  public stringifyValue(item: TItem): string {
    const value = this.getValue(item);

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    return '';
  }

  public onToggleArrowKeydown(event: KeyboardEvent, dropdown: { show(): void }, focusLastItem: boolean): void {
    showDropdownAndFocusMenuItem(event, () => dropdown.show(), focusLastItem);
  }

  public onMenuKeydown(event: KeyboardEvent, dropdown: { hide(): void }, toggleButton: HTMLElement): void {
    handleDropdownMenuContainerKeydown(event, () => dropdown.hide(), toggleButton);
  }

  private syncSelectedItem(): void {
    if (!this.items) {
      this.selectedItemField = undefined;

      return;
    }

    this.selectedItemField = this.items.find((item) => this.getValue(item) === this.model);
  }
}
